import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiClock, FiPlus, FiTrash2, FiCheck, FiChevronLeft, FiX } from "react-icons/fi";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;
const WORKDAYS_GET = `${BASE_URL}/service/my-workdays/`;
const WORKDAYS_UPDATE = `${BASE_URL}/service/workdays-update/`;
const WORKDAYS_CREATE = `${BASE_URL}/service/workdays-create/`;

const daysList = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const defaultTimes = { start_time: "9:00am", end_time: "6:00pm" };

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

// Функции из мобильного приложения
const parseTimeToDate = (timeStr) => {
  if (!timeStr) return null;
  
  const timeStrLower = timeStr.toLowerCase();
  const [timePart, meridian] = [timeStrLower.slice(0, -2), timeStrLower.slice(-2)];
  
  let [hours, minutes] = timePart.split(":").map(Number);
  
  if (meridian === 'pm' && hours !== 12) {
    hours += 12;
  } else if (meridian === 'am' && hours === 12) {
    hours = 0;
  }
  
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const toPm = (date) => {
  if (!date || !(date instanceof Date)) return "9:00am";
  
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridian = hours >= 12 ? 'pm' : 'am';
  
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  
  return `${hours}:${minutes.toString().padStart(2, '0')}${meridian}`;
};

const amPmToDayjs = (timeAmPm) => {
  if (!timeAmPm) return dayjs().hour(9).minute(0);
  
  const date = parseTimeToDate(timeAmPm);
  if (!date) return dayjs().hour(9).minute(0);
  
  return dayjs(date);
};

const dayjsToAmPm = (dayjsTime) => {
  if (!dayjsTime || !dayjsTime.isValid()) return "9:00am";
  
  const date = dayjsTime.toDate();
  return toPm(date);
};

const convertTo24HourFormat = (timeStr) => {
  const date = parseTimeToDate(timeStr);
  if (!date) return "00:00:00";
  
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:00`;
};

const hasOverlap = (ranges) => {
  if (ranges.length <= 1) return false;
  
  const sorted = [...ranges].sort((a, b) => {
    const timeA = parseTimeToDate(a.start_time)?.getTime() || 0;
    const timeB = parseTimeToDate(b.start_time)?.getTime() || 0;
    return timeA - timeB;
  });
  
  for (let i = 0; i < sorted.length - 1; i++) {
    const endA = parseTimeToDate(sorted[i].end_time)?.getTime() || 0;
    const startB = parseTimeToDate(sorted[i + 1].start_time)?.getTime() || 0;
    
    if (endA > startB) return true;
  }
  
  return false;
};

const displayTime = (timeAmPm) => {
  if (!timeAmPm) return "--:--";
  return timeAmPm.replace('am', ' AM').replace('pm', ' PM');
};

// CSS стили
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.2s ease-out forwards;
    opacity: 0;
  }
  
  @keyframes slideIn {
    from {
      transform: translateX(-10px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .animate-slideIn {
    animation: slideIn 0.3s ease-out forwards;
  }
`;

export default function EditWorkingHoursModal({ onClose, selectedDay = null, initialData = null }) {
  const [schedule, setSchedule] = useState(
    daysList.map((day) => ({
      day: day,
      active: false,
      dayTimeRanges: [{
        start_time: defaultTimes.start_time,
        end_time: defaultTimes.end_time,
      }]
    }))
  );
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [editingSingleDay, setEditingSingleDay] = useState(false);
  const userStatus = localStorage.getItem("user_status");

  useEffect(() => {
    setIsVisible(true);
    
    if (selectedDay && initialData) {
      // Режим редактирования одного дня
      setEditingSingleDay(true);
      const dayData = {
        day: initialData.day,
        active: initialData.active,
        dayTimeRanges: initialData.dayTimeRanges.length > 0 
          ? initialData.dayTimeRanges.map(r => {
              let startTime = r.start_time;
              let endTime = r.end_time;
              
              if (startTime && !startTime.includes('am') && !startTime.includes('pm')) {
                const [hours, minutes] = startTime.split(':').map(Number);
                const date = new Date();
                date.setHours(hours, minutes, 0, 0);
                startTime = toPm(date);
              }
              if (endTime && !endTime.includes('am') && !endTime.includes('pm')) {
                const [hours, minutes] = endTime.split(':').map(Number);
                const date = new Date();
                date.setHours(hours, minutes, 0, 0);
                endTime = toPm(date);
              }
              
              return {
                start_time: startTime || defaultTimes.start_time,
                end_time: endTime || defaultTimes.end_time,
              };
            })
          : [{
              start_time: defaultTimes.start_time,
              end_time: defaultTimes.end_time,
            }]
      };
      setSchedule([dayData]);
    } else {
      fetchWorkdays();
    }
    
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [selectedDay, initialData]);

  const toggleDay = (index) => {
    setSchedule((prev) =>
      prev.map((day, i) => {
        if (i === index) {
          const newActive = !day.active;
          return {
            ...day,
            active: newActive,
            dayTimeRanges: newActive ? [{
              start_time: defaultTimes.start_time,
              end_time: defaultTimes.end_time,
            }] : []
          };
        }
        return day;
      })
    );
  };

  const handleTimeChange = (dayIndex, rangeIndex, timePosition, dayjsValue) => {
    if (!dayjsValue || !dayjsValue.isValid()) return;
    
    const formatted = dayjsToAmPm(dayjsValue);
    
    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      const newRanges = [...selectedDay.dayTimeRanges];
      
      newRanges[rangeIndex] = { 
        ...newRanges[rangeIndex], 
        [timePosition]: formatted 
      };
      
      const current = newRanges[rangeIndex];
      const startTime = parseTimeToDate(current.start_time)?.getTime() || 0;
      const endTime = parseTimeToDate(current.end_time)?.getTime() || 0;
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const endOfDayTime = endOfDay.getTime();
      
      if (endTime > endOfDayTime || startTime > endOfDayTime) {
        toast.error("Work time cannot go past 11:59 PM.");
        return prev;
      }
      if (endTime <= startTime) {
        toast.error("End time must be later than start time.");
        return prev;
      }
      if (hasOverlap(newRanges)) {
        toast.error("You have already registered for service at this time.");
        return prev;
      }
      
      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });
  };

  const addTimeRange = (dayIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      
      if (!selectedDay.active) {
        toast.error(`Please enable ${selectedDay.day} before adding a new range.`);
        return prev;
      }
      
      const ranges = [...selectedDay.dayTimeRanges];
      if (!ranges.length) {
        toast.error("No time ranges available.");
        return prev;
      }
      
      const HOUR = 60 * 60 * 1000;
      const lastEndStr = ranges[ranges.length - 1].end_time;
      const lastEnd = parseTimeToDate(lastEndStr);
      
      if (!lastEnd) {
        toast.error("Invalid last end time.");
        return prev;
      }
      
      const dayEnd = new Date(lastEnd);
      dayEnd.setHours(23, 59, 0, 0);
      
      const startDt = new Date(lastEnd.getTime() + HOUR);
      if (startDt > dayEnd) {
        toast.error("Cannot add more ranges — reached end of day.");
        return prev;
      }
      
      const endCandidate = new Date(lastEnd.getTime() + 2 * HOUR);
      const endDt = endCandidate > dayEnd ? dayEnd : endCandidate;
      
      const newRange = {
        start_time: toPm(startDt),
        end_time: toPm(endDt),
      };
      
      const newRanges = [...ranges, newRange];
      if (hasOverlap(newRanges)) {
        toast.error("Time ranges cannot overlap.");
        return prev;
      }
      
      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });
  };

  const deleteTimeRange = (dayIndex, rangeIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      
      if (selectedDay.dayTimeRanges.length <= 1) {
        selectedDay.dayTimeRanges = [{
          start_time: defaultTimes.start_time,
          end_time: defaultTimes.end_time,
        }];
      } else {
        selectedDay.dayTimeRanges = selectedDay.dayTimeRanges.filter(
          (_, i) => i !== rangeIndex
        );
      }
      
      updated[dayIndex] = selectedDay;
      return updated;
    });
  };

  const canAddTimeRange = (dayIndex) => {
    const day = schedule[dayIndex];
    if (!day.active || day.dayTimeRanges.length === 0) return false;
    
    const lastRange = day.dayTimeRanges[day.dayTimeRanges.length - 1];
    const lastEndStr = lastRange.end_time;
    const lastEnd = parseTimeToDate(lastEndStr);
    
    if (!lastEnd) return false;
    
    const HOUR = 60 * 60 * 1000;
    const dayEnd = new Date(lastEnd);
    dayEnd.setHours(23, 59, 0, 0);
    
    const startDt = new Date(lastEnd.getTime() + HOUR);
    return startDt <= dayEnd;
  };

  const getValidToken = async () => {
    let token = localStorage.getItem("accessToken");
    if (!token) {
      token = sessionStorage.getItem("accessToken");
    }
    return token;
  };

  const fetchWorkdays = async () => {
    try {
      const token = await getValidToken();
      if (!token) {
        console.log("No token found");
        return;
      }

      const headers = buildHeaders(token);
      
      const res = await axios.get(WORKDAYS_GET, { headers });
      const data = res.data;

      if (data && data.length > 0) {
        const newSchedule = daysList.map(dayName => {
          const dayData = data.find(d => d.day === dayName);
          if (dayData) {
            return {
              day: dayData.day,
              active: dayData.active,
              dayTimeRanges: dayData.dayTimeRanges.length > 0 
                ? dayData.dayTimeRanges.map(r => {
                    let startTime = r.start_time;
                    let endTime = r.end_time;
                    
                    if (startTime && !startTime.includes('am') && !startTime.includes('pm')) {
                      const [hours, minutes] = startTime.split(':').map(Number);
                      const date = new Date();
                      date.setHours(hours, minutes, 0, 0);
                      startTime = toPm(date);
                    }
                    if (endTime && !endTime.includes('am') && !endTime.includes('pm')) {
                      const [hours, minutes] = endTime.split(':').map(Number);
                      const date = new Date();
                      date.setHours(hours, minutes, 0, 0);
                      endTime = toPm(date);
                    }
                    
                    return {
                      start_time: startTime || defaultTimes.start_time,
                      end_time: endTime || defaultTimes.end_time,
                    };
                  })
                : [{
                    start_time: defaultTimes.start_time,
                    end_time: defaultTimes.end_time,
                  }]
            };
          }
          
          return {
            day: dayName,
            active: false,
            dayTimeRanges: [{
              start_time: defaultTimes.start_time,
              end_time: defaultTimes.end_time,
            }]
          };
        });
        
        setSchedule(newSchedule);
      }
    } catch (err) {
      console.error("Error fetching workdays:", err);
      if (err.response?.status !== 404 && err.response?.status !== 401) {
        toast.error("Error fetching workdays");
      }
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (userStatus !== "salon" && !editingSingleDay) {
        const activeDays = schedule.filter(d => d.active);
        if (activeDays.length === 0) {
          toast.error("Please enable at least one working day.");
          setLoading(false);
          return;
        }
      }

      const token = await getValidToken();
      if (!token) {
        toast.error("No access token found. Please log in.");
        setLoading(false);
        return;
      }

      let payload;
      
      if (editingSingleDay) {
        // Сохраняем только один день
        const day = schedule[0];
        payload = [{
          day: day.day,
          active: day.active,
          dayTimeRanges: day.active
            ? day.dayTimeRanges.map(({ start_time, end_time }) => ({
                start_time: convertTo24HourFormat(start_time),
                end_time: convertTo24HourFormat(end_time),
              }))
            : []
        }];
      } else {
        // Сохраняем все дни
        payload = schedule.map((day) => ({
          day: day.day,
          active: day.active,
          dayTimeRanges: day.active
            ? day.dayTimeRanges.map(({ start_time, end_time }) => ({
                start_time: convertTo24HourFormat(start_time),
                end_time: convertTo24HourFormat(end_time),
              }))
            : []
        }));
      }

      // Используем buildHeaders для создания заголовков
      const headers = buildHeaders(token);

      let res;
      try {
        res = await axios.put(WORKDAYS_UPDATE, payload, { headers });
      } catch (err) {
        if (err.response?.status === 404) {
          res = await axios.post(WORKDAYS_CREATE, payload, { headers });
        } else {
          throw err;
        }
      }

      toast.success(editingSingleDay 
        ? `${schedule[0].day.charAt(0).toUpperCase() + schedule[0].day.slice(1)} hours updated successfully!`
        : "Working hours updated successfully!"
      );
      handleClose();
    } catch (err) {
      console.error("Error saving workdays:", err);
      
      if (err.response?.status === 400) {
        toast.error("Invalid data format. Please check your time entries.");
      } else if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else if (err.response?.status === 500) {
        toast.error("Server error. Please check your time values and try again.");
      } else {
        toast.error("Error saving working hours. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  const getActiveDaysCount = () => {
    return schedule.filter(day => day.active).length;
  };

  const getDayDisplayName = (dayName) => {
    return dayName.charAt(0).toUpperCase() + dayName.slice(1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
          isVisible 
            ? 'bg-black/50 backdrop-blur-sm' 
            : 'bg-transparent backdrop-blur-none'
        }`}
        onClick={handleClose}
      >
        <div 
          className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300 ${
            isVisible 
              ? 'scale-100 opacity-100 translate-y-0' 
              : 'scale-95 opacity-0 translate-y-4'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {editingSingleDay 
                    ? `Edit ${getDayDisplayName(schedule[0]?.day || '')}`
                    : 'Working Hours'
                  }
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {editingSingleDay 
                    ? "Set specific hours for this day"
                    : `${getActiveDaysCount()} of 7 days active`
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-3">
              {(editingSingleDay ? schedule : schedule).map((day, dayIndex) => (
                <div 
                  key={day.day}
                  className={`border rounded-xl p-4 transition-all duration-200 animate-slideIn ${
                    day.active 
                      ? 'border-gray-800 bg-gray-800/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Day header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleDay(dayIndex)}
                        className={`relative w-12 h-6 rounded-full transition-all duration-300 ${
                          day.active ? 'bg-[#5563F6]' : 'bg-gray-300'
                        }`}
                      >
                        <div 
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transform transition-all duration-300 ${
                            day.active ? 'translate-x-7' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className={`font-medium transition-colors ${
                        day.active ? 'text-gray-900' : 'text-gray-600'
                      }`}>
                        {getDayDisplayName(day.day)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Time ranges */}
                  <div className={`overflow-hidden transition-all duration-300 ${
                    day.active ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="space-y-3 pt-3">
                      {day.dayTimeRanges.map((range, rangeIndex) => (
                        <div 
                          key={rangeIndex}
                          className="flex items-center space-x-3 animate-fadeIn"
                          style={{ animationDelay: `${rangeIndex * 50}ms` }}
                        >
                          {/* Start time */}
                          <div className="flex-0">
                            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-white hover:border-gray-400 transition-colors w-[169px]">
                              <FiClock className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                              <MobileTimePicker
                                value={amPmToDayjs(range.start_time)}
                                onChange={(newValue) => handleTimeChange(dayIndex, rangeIndex, "start_time", newValue)}
                                ampm={true}
                                slotProps={{
                                  textField: {
                                    variant: "standard",
                                    InputProps: {
                                      disableUnderline: true,
                                      className: "w-full outline-none bg-transparent text-gray-900"
                                    }
                                  }
                                }}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    border: 'none',
                                    padding: 0
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: 0,
                                    fontSize: '14px',
                                    fontWeight: 500
                                  },
                                  width: '100%'
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Separator */}
                          <div className="text-gray-400 ml-2.5">–</div>
                          
                          {/* End time */}
                          <div className="flex-1">
                            <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 bg-white hover:border-gray-400 transition-colors w-[169px]">
                              <FiClock className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                              <MobileTimePicker
                                value={amPmToDayjs(range.end_time)}
                                onChange={(newValue) => handleTimeChange(dayIndex, rangeIndex, "end_time", newValue)}
                                ampm={true}
                                slotProps={{
                                  textField: {
                                    variant: "standard",
                                    InputProps: {
                                      disableUnderline: true,
                                      className: "w-full outline-none bg-transparent text-gray-900"
                                    }
                                  }
                                }}
                                sx={{
                                  '& .MuiInputBase-root': {
                                    border: 'none',
                                    padding: 0
                                  },
                                  '& .MuiInputBase-input': {
                                    padding: 0,
                                    fontSize: '14px',
                                    fontWeight: 500
                                  },
                                  width: '100%'
                                }}
                              />
                            </div>
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex space-x-1">
                            {rangeIndex === 0 && (
                              <button
                                onClick={() => addTimeRange(dayIndex)}
                                disabled={!day.active || !canAddTimeRange(dayIndex)}
                                className="p-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Add time range"
                              >
                                <FiPlus className="w-4 h-4" />
                              </button>
                            )}
                            
                            {rangeIndex === day.dayTimeRanges.length - 1 && day.dayTimeRanges.length > 1 && (
                              <button
                                onClick={() => deleteTimeRange(dayIndex, rangeIndex)}
                                className="p-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 active:scale-95 transition-all"
                                title="Delete time range"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <div className="p-6 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row justify-end items-end sm:items-center space-y-4 sm:space-y-0">
              
              
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-6 py-2.5 bg-[#5563F6] text-white rounded-lg font-medium hover:bg-[#5563F6] disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 flex items-center space-x-2 min-w-[120px] justify-center"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving</span>
                    </>
                  ) : (
                    <>
                      <FiCheck className="w-4 h-4" />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  );
}